import React from "react";
import * as Styled from "./styles";
import { useClinicalBudget } from "shared/providers/ClinicalBudget";
import { useAuth } from "shared/providers/auth";
import { getPanProf } from "requests/ClinicalBudget";
import { IPanProfData } from "shared/types/ClinicalBudget";
import { BudgetItems } from "../../BudgetItems";
import { BudgetProcedures } from "../../BudgetProcedures";
import { BudgetOdontograma } from "../../BudgetOdontograma";
import { BudgetGruposModal } from "../../BudgetGruposModal";
import { BudgetFacesModal } from "../../BudgetFacesModal";
import { BudgetArcadaModal } from "../../BudgetArcadaModal";
import { BudgetObsModal } from "../../BudgetObsModal";
import { IoInformationCircle, IoArrowForward } from "react-icons/io5";

const ClinicalBudgetMain: React.FC = () => {
  const { user } = useAuth();
  const {
    patientObj,
    totalItens,
    total,
    formatMoeda,
    setStep,
    budgetItems,
    showValues,
  } = useClinicalBudget();

  const [panProfData, setPanProfData] = React.useState<IPanProfData | null>(null);

  React.useEffect(() => {
    const pacId = patientObj?.pac_id;
    const userId = user?.data?.id || "";
    if (pacId && userId) {
      getPanProf(userId, pacId)
        .then(({ data }) => {
          if (data.success && data.data) {
            setPanProfData(data.data);
          }
        })
        .catch(() => {});
    }
  }, [patientObj?.pac_id, user?.data?.id]);

  const handleContinue = () => {
    if (budgetItems.length === 0) {
      alert('Adicione pelo menos um procedimento ao orçamento!');
      return;
    }
    setStep(showValues ? 2 : 3);
  };

  const formatDate = (dateStr: string): string => {
    const [y, m, d] = dateStr.split('-');
    return `${d}/${m}/${y}`;
  };

  const patientName = patientObj?.pac_name || 'Paciente';
  const patientInitial = patientName.charAt(0).toUpperCase();

  return (
    <Styled.Container>
      <Styled.Header>
        <Styled.HeaderInfo>
          <Styled.HeaderAvatar>{patientInitial}</Styled.HeaderAvatar>
          <Styled.HeaderText>
            <h3>{patientName}</h3>
            <p>Orçamento Clínico</p>
          </Styled.HeaderText>
        </Styled.HeaderInfo>
        <Styled.HeaderStats>
          <Styled.HeaderStat>
            <div className="value">{totalItens}</div>
            <div className="label">Itens</div>
          </Styled.HeaderStat>
          {showValues && (
            <Styled.HeaderStat>
              <div className="value">R$ {formatMoeda(total)}</div>
              <div className="label">Total</div>
            </Styled.HeaderStat>
          )}
        </Styled.HeaderStats>
      </Styled.Header>

      {panProfData && (panProfData.tem_pan || panProfData.tem_prof) && (
        <Styled.PanProfBanner>
          <Styled.PanProfLabel>
            Plano Orto{panProfData.dt_orcamento ? ` (${panProfData.dt_orcamento})` : ''}:
          </Styled.PanProfLabel>
          <Styled.PanProfTags>
            {panProfData.tem_pan && (
              panProfData.dt_pan ? (
                <Styled.PanProfTag $status="done">
                  ✔ Panorâmica <strong>{formatDate(panProfData.dt_pan)}</strong>
                </Styled.PanProfTag>
              ) : (
                <Styled.PanProfTag $status="pending">
                  ⚠ Panorâmica <em>Pendente</em>
                </Styled.PanProfTag>
              )
            )}
            {panProfData.tem_prof && (
              panProfData.dt_prof ? (
                <Styled.PanProfTag $status="done">
                  ✔ Profilaxia <strong>{formatDate(panProfData.dt_prof)}</strong>
                </Styled.PanProfTag>
              ) : (
                <Styled.PanProfTag $status="pending">
                  ⚠ Profilaxia <em>Pendente</em>
                </Styled.PanProfTag>
              )
            )}
          </Styled.PanProfTags>
        </Styled.PanProfBanner>
      )}

      <Styled.InfoBanner>
        <IoInformationCircle className="icon" />
        <span>
          Clique em <strong>"Adicionar"</strong> para selecionar os elementos dentários no <strong>Odontograma</strong>.
        </span>
      </Styled.InfoBanner>

      <Styled.Section>
        <Styled.SectionHeader variant="success">
          <h4>🦷 Procedimentos Adicionados</h4>
          <Styled.Badge>{totalItens} item(s)</Styled.Badge>
        </Styled.SectionHeader>
        <BudgetItems />
      </Styled.Section>

      <Styled.Section>
        <Styled.SectionHeader variant="primary">
          <h4>📋 Procedimentos Disponíveis</h4>
        </Styled.SectionHeader>
        <BudgetProcedures />
      </Styled.Section>

      <Styled.BtnFixed>
        <Styled.Button
          variant="primary"
          onClick={handleContinue}
          disabled={budgetItems.length === 0}
        >
          Continuar
          <IoArrowForward size={20} />
        </Styled.Button>
      </Styled.BtnFixed>

      <BudgetOdontograma />
      <BudgetGruposModal />
      <BudgetFacesModal />
      <BudgetArcadaModal />
      <BudgetObsModal />
    </Styled.Container>
  );
};

export { ClinicalBudgetMain };
